import React, { useState, useEffect } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Input, Label, FormGroup, FormFeedback, Row, Col, Spinner,
} from "reactstrap";

/**
 * Create-ticket modal.
 *
 * Requester handling mirrors the backend contract:
 *   • Regular users: the requester field is HIDDEN — the backend stamps the
 *     ticket with their authenticated email. We never send a requester.
 *   • All-ticket users (IT Admin / Support Agent): an optional "Requester
 *     email" field is shown; if left blank the backend uses the caller's own
 *     email. The backend is the final authority either way.
 */
const EMPTY = {
    subject: "",
    description: "",
    priority: 1,
    groupId: "",
    requesterEmail: "",
};

const SupportTicketForm = ({
    isOpen,
    toggle,
    onSubmit,
    submitting,
    priorities = [],
    groups = [],
    allTicketAccess = false,
    accountLabel = "",
}) => {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            setForm(EMPTY);
            setErrors({});
        }
    }, [isOpen]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.subject.trim()) e.subject = "Subject is required.";
        if (!form.description.trim()) e.description = "Description is required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        const payload = {
            subject: form.subject.trim(),
            description: form.description.trim(),
            priority: Number(form.priority) || 1,
        };
        if (form.groupId) payload.groupId = Number(form.groupId);
        if (allTicketAccess && form.requesterEmail.trim())
            payload.requesterEmail = form.requesterEmail.trim();
        await onSubmit(payload);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
            <ModalHeader toggle={toggle}>
                Create ticket{accountLabel ? ` — ${accountLabel}` : ""}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label className="form-label">Subject</Label>
                    <Input
                        value={form.subject}
                        onChange={(e) => set("subject", e.target.value)}
                        invalid={!!errors.subject}
                        disabled={submitting}
                    />
                    <FormFeedback>{errors.subject}</FormFeedback>
                </FormGroup>

                <FormGroup>
                    <Label className="form-label">Description</Label>
                    <Input
                        type="textarea"
                        rows={6}
                        value={form.description}
                        onChange={(e) => set("description", e.target.value)}
                        invalid={!!errors.description}
                        disabled={submitting}
                    />
                    <FormFeedback>{errors.description}</FormFeedback>
                </FormGroup>

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label className="form-label">Priority</Label>
                            <Input
                                type="select"
                                value={form.priority}
                                onChange={(e) => set("priority", e.target.value)}
                                disabled={submitting}
                            >
                                {priorities.map((p) => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label className="form-label">Group (optional)</Label>
                            <Input
                                type="select"
                                value={form.groupId}
                                onChange={(e) => set("groupId", e.target.value)}
                                disabled={submitting}
                            >
                                <option value="">— Unassigned —</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>

                {allTicketAccess && (
                    <FormGroup>
                        <Label className="form-label">
                            Requester email (optional)
                        </Label>
                        <Input
                            type="email"
                            placeholder="Leave blank to file under your own email"
                            value={form.requesterEmail}
                            onChange={(e) => set("requesterEmail", e.target.value)}
                            disabled={submitting}
                        />
                        <div className="text-muted small mt-1">
                            As a support agent you can file on behalf of another
                            requester. Leave blank to use your own account.
                        </div>
                    </FormGroup>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={toggle} disabled={submitting}>
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting && <Spinner size="sm" className="me-1" />}
                    Create ticket
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SupportTicketForm;
